import { isCommentInvoiceLine } from "./commentInvoiceLines";
import { isIchOrganization } from "../constants/ichOrganizations";
import { getOrgId } from "../actions/orgsActions";
import { invoiceItemMatchesProjectOpsBudgetTarget, normalizeProjectOpsContext } from "./projectOpsUtils";


export function getActivePricingContractDocument(pricingContractProto = null) {
	if (!pricingContractProto || !Array.isArray(pricingContractProto.documents) || pricingContractProto.documents.length === 0) {
		return null;
	}

	return pricingContractProto.documents.find((document) => document && document.id === pricingContractProto.activeDocumentId)
		|| pricingContractProto.documents[0]
		|| null;
}

function getTrimmedString(value) {
	return typeof value === "string" ? value.trim() : "";
}

function getEstimateDisplayName(estimate) {
	if (!estimate || typeof estimate !== "object") {
		return "";
	}

	const candidates = [
		estimate.jobDetails?.name,
		estimate.name,
		estimate.label,
		estimate.title,
		estimate.value
	];

	const resolved = candidates.find((value) => getTrimmedString(value) !== "");
	return resolved ? getTrimmedString(resolved) : "";
}

function isManagedContractEnabledForCurrentOrg() {
	return isIchOrganization(getOrgId());
}

const usdCurrencyFormatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD"
});

function getNumericCurrencyValue(value) {
	if (typeof value === "number") {
		return Number.isFinite(value) ? value : null;
	}

	const trimmedValue = getTrimmedString(value);
	if (trimmedValue === "") {
		return null;
	}

	const parentheticalMatch = trimmedValue.match(/^\((.*)\)$/);
	const normalizedValue = (parentheticalMatch ? `-${parentheticalMatch[1]}` : trimmedValue).replace(/[$,\s]/g, "");
	const parsedValue = Number(normalizedValue);

	return Number.isFinite(parsedValue) ? parsedValue : null;
}

function getNumericCurrencyValueOrZero(value) {
	const numericValue = getNumericCurrencyValue(value);
	return numericValue === null ? 0 : numericValue;
}

export function formatCurrencyDisplay(value, fallback = "") {
	const numericValue = getNumericCurrencyValue(value);
	if (numericValue === null) {
		if (typeof value === "string") {
			return getTrimmedString(value);
		}
		return fallback;
	}

	return usdCurrencyFormatter.format(numericValue);
}

function formatPricingContractState(activeDocument) {
	if (!activeDocument || typeof activeDocument !== "object") {
		return "";
	}

	const kind = getTrimmedString(activeDocument.kind);
	const status = getTrimmedString(activeDocument.status).toLowerCase();
	const numberValue = Number(activeDocument.number);
	const number = Number.isFinite(numberValue) ? numberValue : 0;
	const title = getTrimmedString(activeDocument.title) || getTrimmedString(activeDocument.name);

	if (kind === "initialPurchase") {
		if (status === "locked") {
			return "Agreement of Sale Locked";
		}

		if (title !== "") {
			return status ? `${title} (${status.charAt(0).toUpperCase()}${status.slice(1)})` : title;
		}

		return status ? `Initial Purchase (${status.charAt(0).toUpperCase()}${status.slice(1)})` : "Initial Purchase";
	}

	if (kind === "addendum") {
		const addendumTitle = title || (number > 0 ? `Addendum #${number}` : "Addendum");
		if (status === "" || status === "locked") {
			return addendumTitle;
		}
		return `${addendumTitle} (${status.charAt(0).toUpperCase()}${status.slice(1)})`;
	}

	if (title !== "") {
		return status ? `${title} (${status.charAt(0).toUpperCase()}${status.slice(1)})` : title;
	}

	return status !== "" ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : "";
}

export function getPriceQuoteName(source = {}) {
	const indexedName = getTrimmedString(source && source.priceQuoteName);
	if (indexedName !== "") {
		return indexedName;
	}

	const activeDocument = getActivePricingContractDocument(source && source.pricingContractProto);
	if (activeDocument) {
		const activeDocumentTitle = getTrimmedString(activeDocument.title) || getTrimmedString(activeDocument.name);
		if (activeDocumentTitle !== "") {
			return activeDocumentTitle;
		}
	}

	const selectedValue = getTrimmedString(source && source.selectedValue);
	if (Array.isArray(source && source.estimates) && selectedValue !== "") {
		const selectedEstimate = source.estimates.find((estimate) => estimate && estimate.value === selectedValue);
		const selectedEstimateName = getEstimateDisplayName(selectedEstimate);
		if (selectedEstimateName !== "") {
			return selectedEstimateName;
		}
	}

	if (selectedValue === "original") {
		return "Original";
	}

	return selectedValue;
}

export function getPricingContractStateText(source = {}) {
	if (!isManagedContractEnabledForCurrentOrg()) {
		return "";
	}

	const indexedState = getTrimmedString(source && source.pricingContractStateText);
	if (indexedState !== "") {
		return indexedState;
	}

	const activeDocument = getActivePricingContractDocument(source && source.pricingContractProto);
	if (activeDocument) {
		return formatPricingContractState(activeDocument);
	}

	if (source && source.pricingHasManagedContract === false) {
		return "No agreement of sale locked yet";
	}

	return "";
}

export function getPricingBudgetDetailText(source = {}) {
	const summary = getTrimmedString(source && source.pricingBudgetSummaryString);
	const detail = getTrimmedString(source && source.pricingBudgetDetailString);
	if (summary !== "" && detail !== "") {
		return `${summary} • ${detail}`;
	}
	return summary || detail;
}

function getPricingSourceTags(source = {}) {
	return Array.isArray(source && source.tags)
		? source.tags.map(tag => getTrimmedString(tag)).filter(Boolean)
		: [];
}

function getProjectOpsContextForPricingSource(source = {}) {
	const sourceProjectOps = source && source.projectOps && typeof source.projectOps === "object" ? source.projectOps : {};
	const projectContractAnchor = sourceProjectOps.projectContractAnchor || source.projectContractAnchor || null;
	return normalizeProjectOpsContext({
		...sourceProjectOps,
		projectId: sourceProjectOps.projectId || source.projectOpsProjectId || source.projectId || "",
		projectName: sourceProjectOps.projectName || source.projectOpsProjectName || source.projectName || "",
		stageKey: sourceProjectOps.stageKey || source.projectOpsStageKey || "",
		stageLabel: sourceProjectOps.stageLabel || source.projectOpsStageLabel || "",
		artifactType: sourceProjectOps.artifactType || source.projectOpsArtifactType || "",
		contractMode: sourceProjectOps.contractMode || source.projectOpsContractMode || "",
		addendumNumber: sourceProjectOps.addendumNumber || source.projectOpsAddendumNumber || "",
		activeBudgetLineId: sourceProjectOps.activeBudgetLineId || source.projectOpsBudgetTargetLineId || "",
		budgetTargets: sourceProjectOps.budgetTargets || source.projectOpsBudgetTargets || source.budgetTargets || [],
		projectContractAnchor
	});
}

function isProjectOpsAddendumContext(projectOps = {}) {
	const artifactType = getTrimmedString(projectOps.artifactType).toLowerCase();
	const contractMode = getTrimmedString(projectOps.contractMode).toLowerCase();
	return artifactType === "addendum" || contractMode.indexOf("addendum") !== -1 || Number(projectOps.addendumNumber) > 0;
}

function isProjectOpsContributorPricingSource(source = {}, projectOps = getProjectOpsContextForPricingSource(source)) {
	const normalizedTags = getPricingSourceTags(source).map(tag => tag.toLowerCase());
	if (normalizedTags.indexOf("contract-anchor") !== -1) {
		return false;
	}
	if (normalizedTags.indexOf("contract-contributor") !== -1 || getTrimmedString(source.projectOpsPricingScope) === "contribution") {
		return true;
	}
	if (!isProjectOpsAddendumContext(projectOps)) {
		return false;
	}

	const anchor = projectOps.projectContractAnchor || source.projectContractAnchor || null;
	if (!anchor || typeof anchor !== "object") {
		return false;
	}

	const anchorStageKey = getTrimmedString(anchor.stageKey);
	const stageKey = getTrimmedString(projectOps.stageKey);
	if (anchorStageKey !== "" && stageKey !== "" && anchorStageKey !== stageKey) {
		return true;
	}

	return Boolean(anchor.workItemId || anchor.instanceUrl || anchor.activityInstanceId);
}

function getPricingSourceActiveContractDocument(source = {}, projectOps = null) {
	const activeDocument = getActivePricingContractDocument(source && source.pricingContractProto);
	if (!isProjectOpsAddendumContext(projectOps || {}) || !Array.isArray(source && source.pricingContractProto && source.pricingContractProto.documents)) {
		return activeDocument;
	}

	if (activeDocument && activeDocument.kind === "addendum") {
		return activeDocument;
	}

	const addendumNumber = Number(projectOps && projectOps.addendumNumber);
	const addendumDocuments = source.pricingContractProto.documents.filter(document => document && document.kind === "addendum");
	if (Number.isFinite(addendumNumber) && addendumNumber > 0) {
		const numberedDocument = addendumDocuments.find(document => Number(document.number) === addendumNumber);
		if (numberedDocument) {
			return numberedDocument;
		}
	}

	return addendumDocuments[addendumDocuments.length - 1] || activeDocument;
}

function getPricingSourceInvoiceItems(source = {}, activeDocument = getPricingSourceActiveContractDocument(source)) {
	if (Array.isArray(activeDocument && activeDocument.invoiceItems)) {
		return activeDocument.invoiceItems;
	}
	return Array.isArray(source && source.invoiceItems) ? source.invoiceItems : [];
}

function isPricingAllowanceInvoiceItem(invoiceItem = {}) {
	return Boolean(invoiceItem && (invoiceItem.lineType === "allowance" || invoiceItem.recordType === "allowance"));
}

function getPricingInvoiceItemAmount(invoiceItem = {}) {
	if (isCommentInvoiceLine(invoiceItem)) return 0;
	const directValue = getNumericCurrencyValue(invoiceItem.value);
	if (directValue !== null) {
		return directValue;
	}

	const rate = getNumericCurrencyValueOrZero(invoiceItem.rate);
	const qty = getNumericCurrencyValue(invoiceItem.qty);
	const discount = getNumericCurrencyValue(invoiceItem.discount);
	const total = rate * (qty === null ? 1 : qty) * (discount === null ? 1 : discount);
	return Number.isFinite(total) ? +total.toFixed(2) : 0;
}

function getPricingAllowanceBudgetAmount(invoiceItem = {}) {
	const allowance = invoiceItem && invoiceItem.allowance && typeof invoiceItem.allowance === "object" ? invoiceItem.allowance : {};
	const candidates = [
		allowance.amount,
		allowance.budgetAmount,
		invoiceItem.budgetAmount,
		invoiceItem.value,
		invoiceItem.rate,
		invoiceItem.material
	];

	for (let i = 0; i < candidates.length; i += 1) {
		const amount = getNumericCurrencyValue(candidates[i]);
		if (amount !== null) {
			return amount;
		}
	}

	return 0;
}

function getProjectOpsBudgetTargetsForPricingSummary(projectOps = {}, activeDocument = null) {
	const targets = Array.isArray(projectOps.budgetTargets) ? projectOps.budgetTargets.slice() : [];
	const activeBudgetLineId = getTrimmedString(projectOps.activeBudgetLineId || (activeDocument && activeDocument.activeBudgetLineId));
	if (activeBudgetLineId !== "" && !targets.some(target => getTrimmedString(target && target.lineId) === activeBudgetLineId)) {
		targets.unshift({ lineId: activeBudgetLineId, label: "Budget" });
	}
	return targets;
}

function addProjectOpsBudgetRow(budgetRowsByLineId, invoiceItem, fallbackKey) {
	const lineId = getTrimmedString(invoiceItem && invoiceItem.lineId);
	const key = lineId || fallbackKey;
	if (!key || budgetRowsByLineId.has(key)) {
		return;
	}
	budgetRowsByLineId.set(key, invoiceItem);
}

function getProjectOpsBudgetRowsForPricingSummary(projectOps = {}, activeDocument = null, invoiceItems = []) {
	const targets = getProjectOpsBudgetTargetsForPricingSummary(projectOps, activeDocument);
	const activeBudgetLineId = getTrimmedString(projectOps.activeBudgetLineId || (activeDocument && activeDocument.activeBudgetLineId));
	const budgetRowsByLineId = new Map();

	(Array.isArray(invoiceItems) ? invoiceItems : []).forEach((invoiceItem, index) => {
		if (!isPricingAllowanceInvoiceItem(invoiceItem)) {
			return;
		}
		const lineId = getTrimmedString(invoiceItem.lineId);
		if (activeBudgetLineId !== "" && lineId === activeBudgetLineId) {
			addProjectOpsBudgetRow(budgetRowsByLineId, invoiceItem, `budget:${index}`);
			return;
		}
		if (targets.some(target => invoiceItemMatchesProjectOpsBudgetTarget(invoiceItem, target))) {
			addProjectOpsBudgetRow(budgetRowsByLineId, invoiceItem, `budget:${index}`);
		}
	});

	return Array.from(budgetRowsByLineId.values());
}

function projectOpsContributionMatchesSource(invoiceItem = {}, projectOps = {}) {
	const contribution = invoiceItem && invoiceItem.projectOpsContribution && typeof invoiceItem.projectOpsContribution === "object"
		? invoiceItem.projectOpsContribution
		: null;
	if (!contribution) {
		return false;
	}

	const projectId = getTrimmedString(projectOps.projectId);
	const contributionProjectId = getTrimmedString(contribution.projectId);
	if (projectId !== "" && contributionProjectId !== "" && projectId !== contributionProjectId) {
		return false;
	}

	const stageKey = getTrimmedString(projectOps.stageKey);
	const contributionStageKey = getTrimmedString(contribution.stageKey);
	if (stageKey !== "" && contributionStageKey !== "" && stageKey !== contributionStageKey) {
		return false;
	}

	const addendumNumber = Number(projectOps.addendumNumber);
	const contributionAddendumNumber = Number(contribution.addendumNumber);
	if (Number.isFinite(addendumNumber) && addendumNumber > 0 && Number.isFinite(contributionAddendumNumber) && contributionAddendumNumber > 0 && addendumNumber !== contributionAddendumNumber) {
		return false;
	}

	return true;
}

function getProjectOpsContributionRowsForPricingSummary(projectOps = {}, budgetRows = [], invoiceItems = []) {
	const budgetLineIds = new Set(
		budgetRows
			.map(invoiceItem => getTrimmedString(invoiceItem && invoiceItem.lineId))
			.filter(Boolean)
	);
	const activeBudgetLineId = getTrimmedString(projectOps.activeBudgetLineId);
	if (activeBudgetLineId !== "") {
		budgetLineIds.add(activeBudgetLineId);
	}

	return (Array.isArray(invoiceItems) ? invoiceItems : []).filter((invoiceItem) => {
		if (!invoiceItem || isPricingAllowanceInvoiceItem(invoiceItem)) {
			return false;
		}
		const settlesContractLineId = getTrimmedString(invoiceItem.settlesContractLineId);
		return (settlesContractLineId !== "" && budgetLineIds.has(settlesContractLineId))
			|| projectOpsContributionMatchesSource(invoiceItem, projectOps);
	});
}

function getProjectOpsBudgetTotalForPricingSummary(budgetRows = [], contributionRows = []) {
	const budgetsByKey = new Map();
	budgetRows.forEach((invoiceItem, index) => {
		const key = getTrimmedString(invoiceItem && invoiceItem.lineId) || `budget-row:${index}`;
		if (!budgetsByKey.has(key)) {
			budgetsByKey.set(key, getPricingAllowanceBudgetAmount(invoiceItem));
		}
	});

	contributionRows.forEach((invoiceItem, index) => {
		const settlement = invoiceItem && invoiceItem.allowanceSettlement && typeof invoiceItem.allowanceSettlement === "object"
			? invoiceItem.allowanceSettlement
			: {};
		const key = getTrimmedString(settlement.budgetLineId || invoiceItem.settlesContractLineId);
		if (!key || budgetsByKey.has(key)) {
			return;
		}
		const amount = getNumericCurrencyValue(settlement.budgetAmount);
		if (amount !== null) {
			budgetsByKey.set(key, amount);
		} else if (budgetRows.length === 0) {
			budgetsByKey.set(`contribution-budget:${index}`, 0);
		}
	});

	const total = Array.from(budgetsByKey.values()).reduce((sum, amount) => sum + (Number(amount) || 0), 0);
	return +total.toFixed(2);
}

function getIndexedProjectOpsContributionSummary(source = {}, projectOps = getProjectOpsContextForPricingSource(source)) {
	if (!isProjectOpsContributorPricingSource(source, projectOps)) {
		return null;
	}

	const summary = getTrimmedString(source.projectOpsContributionSummaryString || source.pricingBudgetSummaryString);
	const detail = getTrimmedString(source.projectOpsContributionDetailString || source.pricingBudgetDetailString);
	if (summary !== "" || detail !== "") {
		return {
			primaryText: summary || detail,
			detailText: summary !== "" && detail !== "" && summary !== detail ? detail : "",
			secondaryText: ""
		};
	}

	const spentTotal = getNumericCurrencyValue(source.projectOpsContributionTotal);
	const budgetTotal = getNumericCurrencyValue(source.projectOpsBudgetTotal);
	if (spentTotal === null && budgetTotal === null) {
		return null;
	}
	const normalizedSpentTotal = spentTotal === null ? 0 : spentTotal;
	const normalizedBudgetTotal = budgetTotal === null ? 0 : budgetTotal;
	const primaryText = normalizedSpentTotal > 0
		? `Spent ${formatCurrencyDisplay(normalizedSpentTotal)}`
		: `Budget ${formatCurrencyDisplay(normalizedBudgetTotal)}`;
	let detailText = "";
	if (normalizedBudgetTotal > 0) {
		const delta = +(normalizedSpentTotal - normalizedBudgetTotal).toFixed(2);
		const deltaText = delta > 0
			? `Over budget ${formatCurrencyDisplay(delta)}`
			: `Remaining ${formatCurrencyDisplay(Math.abs(delta))}`;
		detailText = `Budget ${formatCurrencyDisplay(normalizedBudgetTotal)} • Spent ${formatCurrencyDisplay(normalizedSpentTotal)} • ${deltaText}`;
	} else if (normalizedSpentTotal > 0) {
		detailText = `Spent ${formatCurrencyDisplay(normalizedSpentTotal)}`;
	}

	return {
		primaryText,
		detailText,
		secondaryText: "",
		budgetTotal: normalizedBudgetTotal,
		spentTotal: normalizedSpentTotal,
		delta: +(normalizedSpentTotal - normalizedBudgetTotal).toFixed(2)
	};
}

export function getProjectOpsContributionPricingSummary(source = {}) {
	const projectOps = getProjectOpsContextForPricingSource(source);
	if (!isProjectOpsContributorPricingSource(source, projectOps)) {
		return null;
	}

	const activeDocument = getPricingSourceActiveContractDocument(source, projectOps);
	const invoiceItems = getPricingSourceInvoiceItems(source, activeDocument);
	if (invoiceItems.length > 0) {
		const budgetRows = getProjectOpsBudgetRowsForPricingSummary(projectOps, activeDocument, invoiceItems);
		const contributionRows = getProjectOpsContributionRowsForPricingSummary(projectOps, budgetRows, invoiceItems);
		const spentTotal = +contributionRows.reduce((sum, invoiceItem) => sum + getPricingInvoiceItemAmount(invoiceItem), 0).toFixed(2);
		const budgetTotal = getProjectOpsBudgetTotalForPricingSummary(budgetRows, contributionRows);

		if (spentTotal !== 0 || budgetTotal !== 0) {
			const primaryText = spentTotal > 0
				? `Spent ${formatCurrencyDisplay(spentTotal)}`
				: `Budget ${formatCurrencyDisplay(budgetTotal)}`;
			let detailText = "";
			if (budgetTotal > 0) {
				const delta = +(spentTotal - budgetTotal).toFixed(2);
				const deltaText = delta > 0
					? `Over budget ${formatCurrencyDisplay(delta)}`
					: `Remaining ${formatCurrencyDisplay(Math.abs(delta))}`;
				detailText = `Budget ${formatCurrencyDisplay(budgetTotal)} • Spent ${formatCurrencyDisplay(spentTotal)} • ${deltaText}`;
			} else if (spentTotal > 0) {
				detailText = `Spent ${formatCurrencyDisplay(spentTotal)}`;
			}

			return {
				primaryText,
				secondaryText: "",
				detailText,
				budgetTotal,
				spentTotal,
				delta: +(spentTotal - budgetTotal).toFixed(2)
			};
		}
	}

	return getIndexedProjectOpsContributionSummary(source, projectOps);
}

export function formatPricingSummary(totalPriceString, source = {}) {
	const normalizedTotalPriceString = formatCurrencyDisplay(totalPriceString);
	const priceQuoteName = getPriceQuoteName(source);

	if (normalizedTotalPriceString !== "" && priceQuoteName !== "") {
		return `${normalizedTotalPriceString} ${priceQuoteName}`;
	}

	return normalizedTotalPriceString || priceQuoteName;
}

export function getPricingSummaryLines(totalPriceString, source = {}) {
	const normalizedTotalPriceString = formatCurrencyDisplay(totalPriceString);
	const priceQuoteName = getPriceQuoteName(source);
	const pricingContractStateText = getPricingContractStateText(source);
	const projectOpsContributionSummary = getProjectOpsContributionPricingSummary(source);
	const detailText = getPricingBudgetDetailText(source);
	const hasManagedContract = Boolean(
		source?.pricingHasManagedContract === true
		|| getActivePricingContractDocument(source?.pricingContractProto)
	);

	if (projectOpsContributionSummary && projectOpsContributionSummary.primaryText) {
		return {
			primaryText: projectOpsContributionSummary.primaryText,
			secondaryText: pricingContractStateText || priceQuoteName || projectOpsContributionSummary.secondaryText || "",
			detailText: projectOpsContributionSummary.detailText || detailText
		};
	}

	if (normalizedTotalPriceString !== "") {
		if (hasManagedContract) {
			return {
				primaryText: normalizedTotalPriceString,
				secondaryText: pricingContractStateText || priceQuoteName,
				detailText
			};
		}

		if (pricingContractStateText !== "") {
			return {
				primaryText: normalizedTotalPriceString,
				secondaryText: pricingContractStateText,
				detailText
			};
		}

		if (priceQuoteName !== "") {
			return {
				primaryText: normalizedTotalPriceString,
				secondaryText: priceQuoteName,
				detailText
			};
		}

		return {
			primaryText: normalizedTotalPriceString,
			secondaryText: "",
			detailText
		};
	}

	if (priceQuoteName !== "" && pricingContractStateText !== "" && priceQuoteName !== pricingContractStateText) {
		return {
			primaryText: priceQuoteName,
			secondaryText: pricingContractStateText,
			detailText
		};
	}

	return {
		primaryText: priceQuoteName || pricingContractStateText,
		secondaryText: "",
		detailText
	};
}
